import React from 'react'

import { PropertyType } from '~/client/graph/types.generated'
import { ResourceProperty } from '~/client/src/desktop/views/ProjectSetUp/components/ProjectSetupResourceConfig/ResourceProperties/types'
import UserOrBrandCell from '~/client/src/desktop/views/ProjectSetUp/components/ProjectSetupResourceConfig/UserOrBrandCell'
import BaseStruxhubInput from '~/client/src/shared/components/StruxhubInputs/BaseStruxhubInput'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import StruxhubSearchableSelect from '~/client/src/shared/components/StruxhubInputs/StruxhubSearchableSelect'
import { brandName } from '~/client/src/shared/localization/brand'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NOOP } from '~/client/src/shared/utils/noop'
import { formatPropertyType } from '~/client/src/shared/utils/propertyTypeUtils'

import PropertyAccordion from '../ui/PropertyAccordion/PropertyAccordion'

const PROPERTY_TYPES = [
  PropertyType.Text,
  PropertyType.Number,
  PropertyType.Select,
  PropertyType.Hierarchy,
  PropertyType.Checkbox,
  PropertyType.Datetime,
  PropertyType.User,
  PropertyType.Company,
  PropertyType.File,
  PropertyType.ResourceLink,
] as const

interface IGeneralPropertyConfigProps {
  property: ResourceProperty
  label: string
  onLabelChange: (value: string) => void
  onLabelBlur?: () => void
  name?: string
  onNameChange?: (value: string) => void
  type?: PropertyType
  onTypeChange?: (value: PropertyType) => void
  isNameValid?: boolean
  nameValidationMessage?: string
  isCreateMode?: boolean
  createdBy?: string | null
}

const GeneralPropertyConfig: React.FC<IGeneralPropertyConfigProps> = ({
  property,
  label,
  onLabelChange,
  onLabelBlur,
  name,
  onNameChange,
  type,
  onTypeChange,
  isNameValid = true,
  nameValidationMessage = '',
  isCreateMode,
  createdBy,
}) => {
  const selectedType = type || property.propertyType
  const isNameEditable = !!onNameChange
  const isTypeEditable = !!onTypeChange

  const propertyTypeOptions = React.useMemo(() => {
    const baseOptions = PROPERTY_TYPES.map(typeValue => ({
      value: typeValue,
      label:
        typeValue === PropertyType.ResourceLink
          ? Localization.translator.resourcePropertyLocation
          : formatPropertyType(typeValue),
    }))

    if (
      selectedType &&
      !PROPERTY_TYPES.some(typeValue => typeValue === selectedType)
    ) {
      return [
        {
          value: selectedType,
          label: formatPropertyType(selectedType, property.configuration),
        },
        ...baseOptions,
      ]
    }

    return baseOptions
  }, [selectedType, property.configuration])

  return (
    <PropertyAccordion
      title={Localization.translator.propertyDetails}
      isDefaultExpanded
    >
      <div className="tw:flex tw:flex-col tw:px-4">
        <StruxhubInput
          label={Localization.translator.label}
          value={label}
          isRequired={isNameEditable}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onLabelChange(e.target.value)
          }
          onBlur={onLabelBlur}
        />

        <StruxhubInput
          label={Localization.translator.internalName}
          value={name || property.name || ''}
          isRequired={isNameEditable}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            isNameEditable ? onNameChange?.(e.target.value) : NOOP()
          }
          isReadOnly={!isNameEditable}
          isValid={isNameValid}
          validationMessage={
            isNameEditable
              ? nameValidationMessage
              : Localization.translator.internalNameCannotBeChanged
          }
        />

        <StruxhubSearchableSelect
          label={Localization.translator.type}
          value={selectedType || ''}
          onChange={value =>
            isTypeEditable ? onTypeChange?.(value as PropertyType) : NOOP()
          }
          options={propertyTypeOptions}
          isReadOnly={!isTypeEditable}
          allowUserCreation={false}
        />

        {!isCreateMode && (
          <BaseStruxhubInput
            label={Localization.translator.createdBy}
            value={createdBy || brandName}
            isReadOnly
            isHelperTextHidden
            isRequiredTextHidden
          >
            {() => (
              <div className="tw:mx-2.5 tw:my-1.5">
                <UserOrBrandCell userId={createdBy} />
              </div>
            )}
          </BaseStruxhubInput>
        )}
      </div>
    </PropertyAccordion>
  )
}

export default GeneralPropertyConfig
